import React, { useEffect, useRef } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import clsx from 'clsx';
import { debounce } from 'lodash';
import { useController } from 'react-hook-form';
import type { FieldPath, FieldValues, Path } from 'react-hook-form';
import { defaultDebounceMilliseconds } from './helpers';
import { type SharedNumberProps } from './types';

type NumberInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: Path<TFieldValues>;
  ariaLabel?: string; // Only needed in cases where text that could label the element is not visible.
} & SharedNumberProps<TFieldValues, TName>;

export default function NumberInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  name,
  ariaLabel,
  className,
  min,
  max,
  disabled,
  onChange,
  debounceOnChange,
  debounceMilliseconds = defaultDebounceMilliseconds
}: NumberInputProps<TFieldValues, TName>): ReactElement {
  const { field } = useController({ name });

  const onChangeDebounced = useRef(
    debounce((e: ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      }
    }, debounceMilliseconds)
  ).current;
  useEffect(() => () => onChangeDebounced.cancel(), [onChangeDebounced]);

  return (
    <input
      {...field}
      id={id}
      aria-label={ariaLabel}
      className={clsx('form-control', className)}
      type="number"
      min={min}
      max={max}
      disabled={disabled}
      onChange={(e: ChangeEvent<HTMLInputElement>): void => {
        field.onChange(e);
        if (onChange) {
          if (debounceOnChange) {
            onChangeDebounced(e);
          } else {
            onChange(e);
          }
        }
      }}
    />
  );
}
